/**
 * Pure helpers for the Space invite UI.
 *
 * Kept free of React so the security-relevant bits can be tested directly. Two
 * of them are load-bearing rather than cosmetic:
 *
 * - {@link spacePathFromSlug} is the only way the invite landing decides where
 *   to navigate. A code holder controls the URL they visit, so treating the
 *   slug from the join response as opaque text would make `/invite/:code` an
 *   open redirect.
 * - {@link buildSpaceInviteUrl} builds the shareable URL. The code is the
 *   secret, so it goes in the path and never in a query string that a referrer
 *   header or analytics hop would carry off-site.
 *
 * @module pages/spaces/spaceInviteUtils
 */

import {
  SPACE_INVITE_CODE_PATTERN,
  SPACE_INVITE_MAX_USES_PRESETS,
  SPACE_SLUG_MAX_LENGTH,
  SPACE_SLUG_PATTERN,
  isInviteGeneratedRole,
  isValidSpaceInviteMaxUses,
  spacePermissionsSubsetOf,
  type PublicSpaceInviteLink,
  type PublicSpaceRole,
  type SpaceInviteExpiry,
  type SpaceInviteHistoryItem,
  type SpacePermission,
  type SpaceTemporaryAccessDuration,
} from '@adieuu/shared';

/** Route prefix for the public landing page. */
export const SPACE_INVITE_PATH_PREFIX = '/invite';

/**
 * In-app path for a Space, or `null` when the slug is not one we recognise.
 *
 * Callers must treat `null` as "stay put". Anything that is not a bare slug —
 * an absolute URL, a protocol-relative `//evil.example`, a traversal, a
 * newline — is refused rather than sanitised, because there is no legitimate
 * reason for the API to return one and guessing an intent is how redirects get
 * reintroduced.
 */
export function spacePathFromSlug(slug: string | null | undefined): string | null {
  if (typeof slug !== 'string') return null;
  const trimmed = slug.trim();
  if (!trimmed || trimmed.length > SPACE_SLUG_MAX_LENGTH) return null;
  if (!SPACE_SLUG_PATTERN.test(trimmed)) return null;
  return `/s/${trimmed}`;
}

/** Whether a string is shaped like an invite code, before spending a request. */
export function isSpaceInviteCodeShape(code: string | null | undefined): boolean {
  return typeof code === 'string' && SPACE_INVITE_CODE_PATTERN.test(code);
}

/**
 * Absolute shareable URL for a code.
 *
 * `origin` normally comes from `window.location.origin`; it is a parameter so
 * this stays testable and so a desktop build can substitute its web origin.
 */
export function buildSpaceInviteUrl(code: string, origin: string): string {
  const base = origin.replace(/\/+$/, '');
  return `${base}${SPACE_INVITE_PATH_PREFIX}/${encodeURIComponent(code)}`;
}

/** Expiry choices offered when creating an invite, in ascending order. */
export const SPACE_INVITE_EXPIRY_CHOICES: readonly SpaceInviteExpiry[] = [
  '30m',
  '1h',
  '6h',
  '12h',
  '1d',
  '7d',
  '30d',
  'never',
];

/** Temporary-membership choices offered when creating an invite. */
export const SPACE_TEMPORARY_ACCESS_CHOICES: readonly SpaceTemporaryAccessDuration[] = [
  '30m',
  '1h',
  '6h',
  '1d',
  '7d',
  '30d',
];

/** Use-cap presets offered for links. `null` is rendered as "no limit". */
export const SPACE_INVITE_MAX_USES_CHOICES: readonly (number | null)[] = [
  null,
  ...SPACE_INVITE_MAX_USES_PRESETS,
];

/** Select value for unlimited uses (`maxUses: null`). */
export const SPACE_INVITE_MAX_USES_UNLIMITED = 'unlimited';

/** Select value for a typed use cap rather than a preset. */
export const SPACE_INVITE_MAX_USES_CUSTOM = 'custom';

const PRESET_COUNTS = new Set<number>(SPACE_INVITE_MAX_USES_PRESETS);

/** Whether a numeric cap is one of the create-link presets (not Custom). */
export function isSpaceInviteMaxUsesPresetCount(maxUses: number): boolean {
  return PRESET_COUNTS.has(maxUses);
}

/**
 * Parse the Custom use-cap field. Empty and non-integers yield `undefined`
 * so the caller can tell "not yet a number" from unlimited (`null`).
 */
export function parseSpaceInviteMaxUsesDraft(draft: string): number | undefined {
  const trimmed = draft.trim();
  if (trimmed === '') return undefined;
  if (!/^\d+$/.test(trimmed)) return undefined;
  const n = Number(trimmed);
  if (!Number.isSafeInteger(n)) return undefined;
  return n;
}

/** Select value for the max-uses dropdown, including the Custom sentinel. */
export function spaceInviteMaxUsesSelectValue(maxUses: number | null, custom: boolean): string {
  if (custom || (maxUses != null && !isSpaceInviteMaxUsesPresetCount(maxUses))) {
    return SPACE_INVITE_MAX_USES_CUSTOM;
  }
  if (maxUses == null) return SPACE_INVITE_MAX_USES_UNLIMITED;
  return String(maxUses);
}

/**
 * Use cap to send when creating a link. `undefined` means the Custom field is
 * empty or out of range and the request must not go out.
 */
export function spaceInviteMaxUsesForSubmit(options: {
  maxUses: number | null;
  maxUsesCustom: boolean;
  maxUsesDraft: string;
}): number | null | undefined {
  const selectValue = spaceInviteMaxUsesSelectValue(options.maxUses, options.maxUsesCustom);
  if (selectValue === SPACE_INVITE_MAX_USES_CUSTOM) {
    const parsed = parseSpaceInviteMaxUsesDraft(options.maxUsesDraft);
    if (parsed == null || !isValidSpaceInviteMaxUses(parsed)) return undefined;
    return parsed;
  }
  if (!isValidSpaceInviteMaxUses(options.maxUses)) return undefined;
  return options.maxUses;
}

/**
 * Whether a link is still usable right now.
 *
 * This mirrors the server's gate so the Manage list can show a lapsed link as
 * expired without waiting for the sweeper. It is presentation only — the server
 * re-checks on every redeem, and this must never be used to decide whether to
 * send a request.
 */
export function isInviteLinkSpent(
  link: Pick<PublicSpaceInviteLink, 'status' | 'expiresAt' | 'maxUses' | 'useCount'>,
  now: Date = new Date(),
): boolean {
  if (link.status !== 'active') return true;
  if (link.expiresAt && new Date(link.expiresAt).getTime() <= now.getTime()) return true;
  if (link.maxUses != null && link.useCount >= link.maxUses) return true;
  return false;
}

/** i18n key suffix describing why a link can no longer be used. */
export type InviteLinkStateKey = 'active' | 'expired' | 'revoked' | 'exhausted';

/** Which of the four states a link is in, for labelling a Manage row. */
export function inviteLinkState(
  link: Pick<PublicSpaceInviteLink, 'status' | 'expiresAt' | 'maxUses' | 'useCount'>,
  now: Date = new Date(),
): InviteLinkStateKey {
  if (link.status === 'revoked') return 'revoked';
  if (link.status === 'expired') return 'expired';
  if (link.expiresAt && new Date(link.expiresAt).getTime() <= now.getTime()) return 'expired';
  if (link.maxUses != null && link.useCount >= link.maxUses) return 'exhausted';
  return 'active';
}

export interface GrantableRolesParams {
  roles: readonly PublicSpaceRole[];
  /** Roles the viewer holds, used to derive their rank. */
  viewerRoleIds: readonly string[];
  viewerPermissions: readonly SpacePermission[];
  viewerIsAdmin: boolean;
  /** Admin can never be granted temporarily; see the module docs on invites. */
  temporary: boolean;
}

/**
 * Roles the viewer may offer on an invite, for populating the picker.
 *
 * This mirrors the server's `authorizeInviteRoleGrants`, and exists so the UI
 * does not present a choice that will be rejected. It is **not** the control:
 * the server checks the same rules on create and re-checks them on redeem, so a
 * client that ignores this list gains nothing.
 *
 * The default Member role is excluded because every joiner receives it anyway.
 */
export function grantableInviteRoles(params: GrantableRolesParams): PublicSpaceRole[] {
  const { roles, viewerRoleIds, viewerPermissions, viewerIsAdmin, temporary } = params;

  const held = new Set(viewerRoleIds);
  let viewerTop: number | null = null;
  for (const role of roles) {
    if (!held.has(role.id)) continue;
    if (viewerTop === null || role.position < viewerTop) viewerTop = role.position;
  }

  const canManageRoles = viewerPermissions.includes('manageRoles');

  return roles.filter((role) => {
    if (role.isDefaultMember) return false;
    if (isInviteGeneratedRole(role)) return false;

    const isAdminRole = role.systemKey === 'admin';
    if (isAdminRole && !viewerIsAdmin) return false;
    // A temporary Admin is a Space that can lose its last Admin to a timer.
    if (isAdminRole && temporary) return false;

    if (!viewerIsAdmin) {
      if (viewerTop === null) return false;
      if (role.position <= viewerTop) return false;
      if (!canManageRoles && !spacePermissionsSubsetOf(role.permissions, viewerPermissions)) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Absolute local date/time for a minted link's expiry, or `null` when the
 * instant cannot be shown. `never` is a missing `expiresAt`, not this helper.
 */
export function formatSpaceInviteExpiryWhen(
  iso: string,
  locale?: string,
): string | null {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return null;
  return d.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * Coarse relative description of an expiry instant.
 *
 * Days only when at least two days remain; otherwise hours. Minutes when
 * under two hours. A precise countdown would imply the client and server
 * clocks agree.
 */
export function relativeExpiry(
  expiresAt: string | null | undefined,
  now: Date = new Date(),
): { kind: 'never' } | { kind: 'past' } | { kind: 'future'; unit: 'd' | 'h' | 'm'; value: number } {
  if (expiresAt == null) return { kind: 'never' };
  const ms = new Date(expiresAt).getTime() - now.getTime();
  if (!Number.isFinite(ms) || ms <= 0) return { kind: 'past' };

  const minutes = Math.floor(ms / 60_000);
  if (minutes >= 2880) return { kind: 'future', unit: 'd', value: Math.floor(minutes / 1440) };
  if (minutes >= 120) return { kind: 'future', unit: 'h', value: Math.floor(minutes / 60) };
  return { kind: 'future', unit: 'm', value: Math.max(minutes, 1) };
}

export type SpaceInviteHistoryRowState =
  | InviteLinkStateKey
  | 'pending'
  | 'accepted'
  | 'declined';

/** Display status for a mixed history row, including virtual link states. */
export function spaceInviteHistoryRowState(
  item: SpaceInviteHistoryItem,
  now: Date = new Date(),
): SpaceInviteHistoryRowState {
  if (item.kind === 'link') return inviteLinkState(item.link, now);
  const rel = relativeExpiry(item.invite.expiresAt, now);
  if (item.invite.status === 'pending' && rel.kind === 'past') return 'expired';
  return item.invite.status;
}

/** Revoke is only offered for outstanding directs and still-usable links. */
export function canRevokeSpaceInviteHistoryRow(
  item: SpaceInviteHistoryItem,
  now: Date = new Date(),
): boolean {
  return spaceInviteHistoryRowState(item, now) === (item.kind === 'link' ? 'active' : 'pending');
}

export type PendingInviteStatusTooltip =
  | { key: 'spaces.invites.statusInvitedBy'; values: { name: string; username: string } }
  | { key: 'spaces.invites.statusInvitedByOther' };

/**
 * Tooltip for an already-pending People-list row.
 *
 * Own invites show no tooltip. Without `manageInvites` the inviter is
 * redacted, so the copy is generic. With that permission the named form is
 * used once a username is available.
 */
export function pendingInviteStatusTooltip(params: {
  own: boolean;
  canManageInvites: boolean;
  inviter?: { displayName?: string; username?: string } | null;
}): PendingInviteStatusTooltip | null {
  if (params.own) return null;
  const username = params.inviter?.username?.trim();
  if (params.canManageInvites && username) {
    const name = params.inviter?.displayName?.trim() || username;
    return { key: 'spaces.invites.statusInvitedBy', values: { name, username } };
  }
  return { key: 'spaces.invites.statusInvitedByOther' };
}

/**
 * Tooltip body for the history "N channels" pill: intro plus one name per line.
 */
export function spaceInviteGrantedChannelsTooltip(
  intro: string,
  names: readonly string[],
): string {
  return [intro, ...names].join('\n');
}

/** `#lounge` when the channel is known; a fallback when it has been deleted. */
export function spaceInviteGrantedChannelLabel(
  channelName: string | undefined,
  unknownLabel: string,
): string {
  const trimmed = channelName?.trim();
  return trimmed ? `#${trimmed}` : unknownLabel;
}
